// components/ContributorEditSharing.jsx — Anamoria SPA
// v1.2 — Consent copy (Oct 4, 2026; copy v0.4): pass ownerName to ConsentCheckbox so the re-include notice resolves {owner}.
// v1.1 — Consent PR E3b (Oct 4, 2026; spec PR-E v0.2 §2 E3; plan v0.26 §8 D9a; copy v0.3 §3.3):
//   per-memory "leave this one out" / re-include of the owner's copy. New OPTIONAL props
//   space, api, keepCopyExcluded, onKeepCopyChange(excluded, shownHash?). Block shown only when the
//   memory is shared (backend ignores contributor_only) AND space.keepCopyCaptureEnabled AND
//   space.keepCopyAllowed — dark otherwise; output byte-identical when the new props are absent.
//   - included → plain checkbox "Leave this one out of {owner}'s copy" → onKeepCopyChange(true)
//     (a withdrawal: no notice needed — ICO: as easy to withdraw as to give).
//   - excluded → status line + ConsentCheckbox (contributor_keep_copy) "Include this in {owner}'s
//     copy again" → onKeepCopyChange(false, shownHash) (a new grant carries the hash — B2; the
//     checkbox hides itself while the wording isn't stored, fail-closed).
// v1.0 — PR-7a / NS-6 (frontend): contributor edit-mode sharing controls.
//
// Reuses the settled sharing model in the EXISTING edit pages (no new view):
//   - current-state pill (Copy v0.4 §7),
//   - scope picker (v0.7 SV-2 + SH-2/SH-3/SH-3a),
//   - "Stop sharing" confirm set (v0.7 SH-4–SH-9). Pull-back is SILENT (PR-4b).
//
// Rendered by the contributor capture pages ONLY in edit mode when group spaces
// is ON. The memory is always the contributor's own (the pencil shows on own
// cards only; the backend enforces creator_contributor_id = me regardless).
//
// Controls are backend-accurate:
//   contributor_only (private)                      -> Share (scope picker).
//   owner_contributor_only OR group/pending (shared,
//     not approved)                                 -> Change tier + Make private.
//   group/approved                                  -> Make private only
//     (backend blocks a direct tier change: ALREADY_APPROVED_USE_REVOKE; the
//      honest path is make-private-then-reshare).
//
// Self-contained inline styles on purpose — the shared member capture modules
// stay untouched (BL-1-safe), matching ContributorSaveActions.

import { useState } from 'react';
import ConsentCheckbox from './ConsentCheckbox';

export function sharingLabel(visibilityPref, reviewStatus) {
  const vp = visibilityPref;
  if (vp === 'contributor_only' || vp == null) return 'Only you';
  if (vp === 'owner_contributor_only') return 'Shared with the space owner';
  if (vp === 'group') {
    if (reviewStatus === 'approved') return 'Shared with the group';
    if (reviewStatus === 'pending') return 'Shared with the space owner · not yet with the group';
    return 'Shared with the space owner'; // rejected / other: silent (Copy v0.4 §7, judgment B)
  }
  return null;
}

export default function ContributorEditSharing({
  visibilityPref,
  reviewStatus,
  groupSpacesEnabled,
  saving,
  onShare,
  onMakePrivate,
  space,              // E3b (optional)
  api,                // E3b (optional)
  keepCopyExcluded,   // E3b (optional)
  onKeepCopyChange,   // E3b (optional)
}) {
  const [picking, setPicking] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [scope, setScope] = useState(visibilityPref === 'group' ? 'group' : 'owner');

  if (!groupSpacesEnabled) return null;

  const isPrivate = visibilityPref === 'contributor_only' || visibilityPref == null;
  const isApprovedGroup = visibilityPref === 'group' && reviewStatus === 'approved';
  const label = sharingLabel(visibilityPref, reviewStatus);
  // E3b: keep-a-copy per-memory block — only for shared memories, flag on, and the owner allowed a copy.
  const showKeepCopy = !isPrivate && !!api && space?.keepCopyCaptureEnabled === true && space?.keepCopyAllowed === true;
  const owner = space?.ownerName || 'the space owner';

  if (picking) {
    return (
      <div style={S.wrap}>
        <h3 style={S.heading}>Who can see this memory?</h3>

        <label style={S.option}>
          <input type="radio" name="edit-scope" checked={scope === 'owner'} onChange={() => setScope('owner')} />
          <span style={S.optText}>
            <span style={S.optTitle}>The space owner</span>
            <span style={S.optGloss}>You and the space owner can see this.</span>
          </span>
        </label>

        <label style={S.option}>
          <input type="radio" name="edit-scope" checked={scope === 'group'} onChange={() => setScope('group')} />
          <span style={S.optText}>
            <span style={S.optTitle}>Everyone in the group</span>
            <span style={S.optGloss}>
              The space owner sees this first, and can then share it with everyone in the group.
            </span>
          </span>
        </label>

        <button
          type="button"
          style={S.primary}
          disabled={saving}
          onClick={() => { onShare(scope === 'group' ? 'group' : 'owner_contributor_only'); setPicking(false); }}
        >
          {saving ? 'Saving…' : 'Save who can see this'}
        </button>
        <button type="button" style={S.ghost} disabled={saving} onClick={() => setPicking(false)}>
          Back
        </button>
      </div>
    );
  }

  return (
    <div style={S.wrap}>
      <div style={S.sectionLabel}>Who can see this</div>
      <div><span style={S.pill}>{label}</span></div>
      {isPrivate && (
        <p style={S.reassure}>Only you can see this. You can share it whenever you&rsquo;re ready.</p>
      )}

      {showKeepCopy && keepCopyExcluded !== true && (
        <label style={S.kcRow}>
          <input
            type="checkbox"
            checked={false}
            disabled={saving}
            onChange={() => onKeepCopyChange?.(true)}
          />
          <span>Leave this one out of {owner}&rsquo;s copy</span>
        </label>
      )}
      {showKeepCopy && keepCopyExcluded === true && (
        <div style={S.kcWrap}>
          <p style={S.kcStatus}>This one is left out of {owner}&rsquo;s copy.</p>
          <ConsentCheckbox
            api={api}
            docType="contributor_keep_copy"
            ownerName={owner}
            label={`Include this in ${owner}’s copy again`}
            checked={false}
            disabled={saving}
            id="edit-keep-copy-reinclude"
            onChange={(checked, shownHash) => { if (checked) onKeepCopyChange?.(false, shownHash); }}
          />
        </div>
      )}

      {isPrivate ? (
        <button type="button" style={S.primary} disabled={saving} onClick={() => setPicking(true)}>
          Share this memory
        </button>
      ) : (
        <>
          {!isApprovedGroup && (
            <button type="button" style={S.quiet} disabled={saving} onClick={() => setPicking(true)}>
              Change who can see this
            </button>
          )}
          <button type="button" style={S.danger} disabled={saving} onClick={() => setConfirming(true)}>
            Make this private
          </button>
        </>
      )}

      {confirming && (
        <div style={S.scrim} role="dialog" aria-modal="true">
          <div style={S.modal}>
            <h3 style={S.modalHeading}>Stop sharing this memory?</h3>
            <p style={S.modalBody}>It will stay in your memories, and from now on only you will see it.</p>
            <button type="button" style={S.primary} disabled={saving} onClick={() => setConfirming(false)}>
              Keep sharing
            </button>
            <button
              type="button"
              style={S.quiet}
              disabled={saving}
              onClick={() => { setConfirming(false); onMakePrivate(); }}
            >
              Stop sharing
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const S = {
  wrap: { display: 'flex', flexDirection: 'column', gap: 10, width: '100%', marginTop: 8 },
  sectionLabel: { fontSize: 12, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--color-sage-dark, #5b7a65)', fontWeight: 700 },
  heading: { margin: '0 0 4px', fontSize: 16, fontWeight: 600 },
  pill: { display: 'inline-flex', alignItems: 'center', background: 'var(--color-cream-2, #EFEADF)', color: '#6b6459', fontSize: 12, fontWeight: 600, padding: '3px 10px', borderRadius: 999, lineHeight: 1.4 },
  reassure: { fontSize: 13, margin: '2px 0 2px', opacity: 0.85 },
  kcRow: { display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 14, cursor: 'pointer', margin: '2px 0' },
  kcWrap: { display: 'flex', flexDirection: 'column', gap: 6, margin: '2px 0' },
  kcStatus: { fontSize: 13, margin: 0, color: '#6b6459' },
  option: { display: 'flex', gap: 10, alignItems: 'flex-start', padding: 12, border: '1px solid #d9d4cc', borderRadius: 12, cursor: 'pointer' },
  optText: { display: 'flex', flexDirection: 'column', gap: 2 },
  optTitle: { fontWeight: 600 },
  optGloss: { fontSize: 13, opacity: 0.8 },
  primary: { width: '100%', padding: '13px 16px', borderRadius: 999, border: 'none', background: 'var(--color-sage-dark, #5b7a65)', color: 'var(--color-text-white, #fff)', fontSize: 15, fontWeight: 600, cursor: 'pointer' },
  ghost: { width: '100%', padding: '12px 16px', borderRadius: 999, border: '1px solid #cfc9c0', background: 'transparent', fontSize: 15, cursor: 'pointer' },
  quiet: { width: '100%', padding: '12px 16px', borderRadius: 999, border: '1px solid #e2ddd3', background: 'transparent', color: '#6b6459', fontSize: 15, fontWeight: 600, cursor: 'pointer' },
  danger: { width: '100%', padding: '12px 16px', borderRadius: 999, border: 'none', background: 'transparent', color: '#a15c4b', fontSize: 15, fontWeight: 600, cursor: 'pointer', textAlign: 'center' },
  scrim: { position: 'fixed', inset: 0, background: 'rgba(30,28,24,0.42)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, zIndex: 1000 },
  modal: { background: '#fff', borderRadius: 16, maxWidth: 400, width: '100%', padding: 22, display: 'flex', flexDirection: 'column', gap: 10, boxShadow: '0 20px 60px rgba(0,0,0,0.25)' },
  modalHeading: { margin: '0 0 4px', fontSize: 18, fontWeight: 600 },
  modalBody: { margin: '0 0 8px', color: '#4a4a4a' },
};
